// Shared utilities and small components used across product pages.
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken } from "../../../lib/auth";

export const C = {
  primary: "#1FA868",
  primaryDark: "#16834D",
  primaryDeep: "#0F5132",
  soft: "#E8F5E9",
  faint: "#F0FAF3",
};

// --- Form primitives ---
export function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-black/[0.06] overflow-x-auto ${className}`}>{children}</div>;
}

export function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
  <div className="w-9 h-9 rounded-xl bg-[#E8F5E9] text-[#1FA868] flex items-center justify-center">
    <Icon size={16} />
  </div>
)}
      <div>
        <h3 className="font-display font-bold text-base text-black tracking-tight uppercase">{title}</h3>
        {hint && <p className="text-xs text-black/45">{hint}</p>}
      </div>
    </div>
  );
}

export function Field({ lbl, required, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1.5">
        {lbl} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-black/45 mt-1">{hint}</div>}
    </div>
  );
}

export function TextInput({ value, onChange, type = "text", placeholder, testid, suffix, disabled }) {
  return (
    <div className="relative">
      <input type={type} value={value ?? ""} disabled={disabled}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder} data-testid={testid}
        className={`w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white ${suffix ? "pr-10" : ""} ${disabled ? "opacity-60" : ""}`} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-black/45">{suffix}</span>}
    </div>
  );
}

export function Select({ value, onChange, options, placeholder = "Choose…", testid, addOn }) {
  return (
    <div className="flex gap-2">
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="flex-1 px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {addOn}
    </div>
  );
}

export function Toggle({ value, onChange, label, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${value ? "border-[#1FA868] bg-[#E8F5E9]" : "border-black/15 bg-white"}`}>
      <span className={`w-4 h-4 rounded-sm flex items-center justify-center text-white text-[10px] ${value ? "bg-[#1FA868]" : "bg-black/15"}`}>
        {value && "✓"}
      </span>
      <span className="text-sm font-semibold text-black">{label || (value ? "Yes" : "No")}</span>
    </button>
  );
}

export function PageHeader({ title, breadcrumb, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">{title}</h1>
        <div className="text-xs text-black/55 mt-1">{breadcrumb}</div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function PrimaryButton({ children, onClick, type, testid, disabled, icon: Icon }) {
  return (
    <button type={type || "button"} onClick={onClick} disabled={disabled} data-testid={testid}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold hover:opacity-95 disabled:opacity-60 shadow-sm">
      {Icon && <Icon size={14} />} {children}
    </button>
  );
}

export function GhostButton({ children, onClick, to, testid, icon: Icon, variant = "default" }) {
  const cls = variant === "danger"
    ? "bg-rose-50 text-rose-600 hover:bg-rose-100"
    : "bg-white border border-black/10 text-black hover:border-black/25";
  const Comp = to ? "a" : "button";
  return (
    <Comp href={to} onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold ${cls}`}>
      {Icon && <Icon size={13} />} {children}
    </Comp>
  );
}

// --- Reusable upload helper ---
export async function uploadProductImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/t/products/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
  return r.json();
}

// --- Generic Taxonomy CRUD page ---
export function TaxonomyCRUD({ endpoint, title, breadcrumb, fields, testidBase, parentEndpoint }) {
  const [rows, setRows] = useState([]);
  const [parents, setParents] = useState([]);
  const [search, setSearch] = useState("");
  const [dlg, setDlg] = useState(null);

  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (parentEndpoint) api.get(parentEndpoint).then((r) => setParents(r.data));
  }, []); // eslint-disable-line

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    await api.delete(`${endpoint}/${r.id}`);
    toast.success("Deleted");
    load();
  };

  const save = async () => {
    const payload = {};
    fields.forEach((f) => { payload[f.k] = dlg[f.k] ?? f.default ?? ""; });
    payload.active = dlg.active !== false;
    if (parentEndpoint) payload.parent_id = dlg.parent_id || null;
    if (!payload.name?.trim()) return toast.error("Name required");
    try {
      if (dlg._editing) {
        await api.patch(`${endpoint}/${dlg._editing}`, payload);
        toast.success("Updated");
      } else {
        await api.post(endpoint, payload);
        toast.success("Created");
      }
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter((r) => !search || r.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8">
      <PageHeader title={title} breadcrumb={breadcrumb}
        actions={<PrimaryButton onClick={() => setDlg({ active: true })} icon={Plus} testid={`${testidBase}-new`}>New</PrimaryButton>} />

      <div className="flex items-center gap-2 bg-white rounded-xl border border-black/10 px-3 py-2 max-w-md mb-4">
        <Search size={16} className="text-black/40" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
          data-testid={`${testidBase}-search`} className="flex-1 bg-transparent focus:outline-none text-sm" />
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Name</th>
              {fields.find((f) => f.k === "short_name") && <th className="text-left p-3.5 font-bold">Short</th>}
              {parentEndpoint && <th className="text-left p-3.5 font-bold">Parent</th>}
              <th className="text-left p-3.5 font-bold">Description</th>
              <th className="text-left p-3.5 font-bold">Status</th>
              <th className="text-right p-3.5 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((r) => {
              const parent = parents.find((p) => p.id === r.parent_id);
              return (
                <tr key={r.id} className="hover:bg-[#FAFBFB]">
                  <td className="p-3.5 font-semibold text-black">{r.name}</td>
                  {fields.find((f) => f.k === "short_name") && <td className="p-3.5 text-black/65">{r.short_name}</td>}
                  {parentEndpoint && <td className="p-3.5 text-black/65">{parent?.name || "—"}</td>}
                  <td className="p-3.5 text-black/55 truncate max-w-xs">{r.description}</td>
                  <td className="p-3.5">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${r.active ? "bg-[#E8F5E9] text-[#1FA868]" : "bg-black/5 text-black/55"}`}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="p-3.5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setDlg({ ...r, _editing: r.id })} data-testid={`${testidBase}-edit-${r.id}`}
                        className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D1F0DA]"><Pencil size={13} /></button>
                      <button onClick={() => remove(r)} data-testid={`${testidBase}-delete-${r.id}`}
                        className="p-2 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-black/45">No {title.toLowerCase()} yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      {dlg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDlg(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-black/8 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl text-black">{dlg._editing ? "Edit" : "New"} {title.replace(/s$/, "").replace(/ies$/, "y")}</h2>
              <button onClick={() => setDlg(null)} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              {fields.map((f) => (
                <Field key={f.k} lbl={f.lbl} required={f.required}>
                  {f.type === "textarea" ? (
                    <textarea value={dlg[f.k] || ""} onChange={(e) => setDlg({ ...dlg, [f.k]: e.target.value })} rows={3}
                      data-testid={`${testidBase}-field-${f.k}`}
                      className="w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
                  ) : (
                    <TextInput value={dlg[f.k] || ""} onChange={(v) => setDlg({ ...dlg, [f.k]: v })}
                      testid={`${testidBase}-field-${f.k}`} />
                  )}
                </Field>
              ))}
              {parentEndpoint && (
                <Field lbl="Parent Category">
                  <Select value={dlg.parent_id || ""} onChange={(v) => setDlg({ ...dlg, parent_id: v })}
                    options={parents.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Choose parent" testid={`${testidBase}-field-parent`} />
                </Field>
              )}
              <Field lbl="Status">
                <Toggle value={dlg.active !== false} onChange={(v) => setDlg({ ...dlg, active: v })}
                  label={dlg.active !== false ? "Active" : "Inactive"} testid={`${testidBase}-field-active`} />
              </Field>
            </div>
            <div className="p-5 border-t border-black/8 flex justify-end gap-2">
              <button onClick={() => setDlg(null)} className="px-4 py-2 rounded-lg border border-black/15 text-sm font-semibold">Cancel</button>
              <PrimaryButton onClick={save} testid={`${testidBase}-save`}>{dlg._editing ? "Save changes" : "Create"}</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Categories / SubCategories / Brands / Units exported instances
export function CategoriesPage() {
  return <TaxonomyCRUD endpoint="/t/categories" title="Categories" breadcrumb="Products · Category" testidBase="categories"
    fields={[{ k: "name", lbl: "Name", required: true }, { k: "description", lbl: "Description", type: "textarea" }]} />;
}
export function SubCategoriesPage() {
  return <TaxonomyCRUD endpoint="/t/subcategories" title="Sub Categories" breadcrumb="Products · Sub Category" testidBase="subcategories"
    parentEndpoint="/t/categories"
    fields={[{ k: "name", lbl: "Name", required: true }, { k: "description", lbl: "Description", type: "textarea" }]} />;
}
export function BrandsPage() {
  return <TaxonomyCRUD endpoint="/t/brands" title="Brands" breadcrumb="Products · Brand" testidBase="brands"
    fields={[{ k: "name", lbl: "Name", required: true }, { k: "description", lbl: "Description", type: "textarea" }]} />;
}
export function WarehousesPage() {
  return <TaxonomyCRUD endpoint="/t/warehouses" title="Branches" breadcrumb="Products · Branch" testidBase="warehouses"
    fields={[
      { k: "name", lbl: "Name", required: true },
      { k: "address", lbl: "Address" },
      { k: "phone", lbl: "Phone" },
      { k: "description", lbl: "Description", type: "textarea" },
    ]} />;
}
export function UnitsPage() {
  return <TaxonomyCRUD endpoint="/t/units" title="Units" breadcrumb="Products · Unit" testidBase="units"
    fields={[
      { k: "name", lbl: "Name", required: true },
      { k: "short_name", lbl: "Short name (e.g. kg)" },
      { k: "base_unit", lbl: "Base unit (optional, e.g. Piece)" },
    ]} />;
}
